'use client';

import { useEffect, useRef, useState } from 'react';

/** Once-visible IntersectionObserver hook for scroll reveals and count-ups. */
export function useInView<T extends Element>(threshold = 0.25) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    // Failsafe: nothing stays invisible for renderers/screenshots that never
    // scroll — off-screen content reveals itself after a beat regardless.
    const failsafe = window.setTimeout(() => {
      setInView(true);
      io.disconnect();
    }, 2500);
    return () => {
      io.disconnect();
      window.clearTimeout(failsafe);
    };
  }, [threshold]);

  return { ref, inView };
}

/**
 * Fires once when an element the caller already has a ref to comes within
 * `rootMargin` of the viewport.
 *
 * Two deliberate differences from `useInView`: it observes an existing ref
 * rather than minting one, and it has **no timeout failsafe**. Callers use this
 * to gate network requests — a failsafe that flips everything true after a beat
 * would fire every request on the page, which is the thing being avoided.
 */
export function useNearViewport<T extends Element>(
  ref: React.RefObject<T | null>,
  initial = false,
  rootMargin = '400px'
) {
  const [near, setNear] = useState(initial);

  useEffect(() => {
    if (near) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, near, rootMargin]);

  return near;
}
